import { useState, useMemo } from 'react';
import '../pages/ProductDetail.css'
import Footer from '../components/Footer'

const isValidEmail = (e) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(e);

const Experience = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // shared UX
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // HOUSE: 1–2 guests
  const [showHouseForm, setShowHouseForm] = useState(false);
  const [housePartySize, setHousePartySize] = useState('1');
  const [houseForm, setHouseForm] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    location: '',
    notes: '',
  });

  // EVENT: 2–5 guests, ~1hr/set
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventGuestCount, setEventGuestCount] = useState('2');
  const [eventForm, setEventForm] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    startTime: '',
    location: '',
    notes: '',
  });

  const totalHours = useMemo(() => parseInt(eventGuestCount || '2', 10), [eventGuestCount]);
  const depositCents = useMemo(() => parseInt(eventGuestCount || '2', 10) * 2500, [eventGuestCount]); // $25/guest

  // ---- submit helpers ----
  const submitHouse = async (e) => {
    e.preventDefault();
    setMsg(null);

    const f = houseForm;
    if (!f.name || !isValidEmail(f.email) || !f.date || !f.location) {
      setMsg({ type: 'error', text: 'Please fill name, email, date, and location.' });
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/nails/experience-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'house',
          name: f.name,
          email: f.email,
          phone: f.phone,
          date: f.date,
          startTime: '', // not needed for house
          guestCount: housePartySize, // 1–2
          location: f.location,
          notes: f.notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to send request');

      setMsg({ type: 'ok', text: 'Got it! I’ll email you to confirm details.' });
      setShowHouseForm(false);
      setHouseForm({ name: '', email: '', phone: '', date: '', location: '', notes: '' });
      setHousePartySize('1');
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const submitEvent = async (e) => {
    e.preventDefault();
    setMsg(null);

    const f = eventForm;
    if (!f.name || !isValidEmail(f.email) || !f.date || !f.startTime || !f.location) {
      setMsg({ type: 'error', text: 'Please fill name, email, date, start time, and location.' });
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/nails/experience-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'event',
          name: f.name,
          email: f.email,
          phone: f.phone,
          date: f.date,
          startTime: f.startTime,
          guestCount: eventGuestCount, // 2–5
          location: f.location,
          notes: f.notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to send request');

      setMsg({ type: 'ok', text: 'Thanks! I’ll email you to confirm your event details.' });
      setShowEventForm(false);
      setEventForm({ name: '', email: '', phone: '', date: '', startTime: '', location: '', notes: '' });
      setEventGuestCount('2');
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const payEventDeposit = async () => {
  setMsg(null);
  if (!isValidEmail(eventForm.email)) {
    setMsg({ type: 'error', text: 'Enter a valid email before paying deposit.' });
    return;
  }
  try {
    setLoading(true);
    console.log('Backend URL:', backendUrl);
    const res = await fetch(`${backendUrl}/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        intent: 'deposit',
        customer_email: eventForm.email,
        optedIn: true,
        items: [], // deposit uses server-calculated line item
        meta: {
          guests: eventGuestCount,      // "2".."5" (server parseInt handles strings)
          date: eventForm.date || '',
          startTime: eventForm.startTime || '',
          location: eventForm.location || '',
          desc: 'Event deposit',
        },
      }),
    });

    // Try to read JSON, but don't crash if it's HTML/text
    let data;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) data = await res.json();
    else data = { error: await res.text() };

    console.log('Deposit response:', res.status, data);

    if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
    if (!data?.url) throw new Error('No checkout URL returned');
    window.location.href = data.url; // go to Stripe Checkout
  } catch (err) {
    setMsg({ type: 'error', text: err.message });
  } finally {
    setLoading(false);
  }
};

  return (
    <>
        <div className="product-detail-wrapper">
    <section className="px-4 py-12 max-w-7xl mx-auto">
      <h2 className="text-3xl md:text-4xl font-bold text-center">The NeNe Nail’d It Experience</h2>
      <p className="text-center max-w-2xl mx-auto mt-2 text-gray-600">
        I pull up and create your custom press-on set live—a mini art show tailored to your vibe, outfit, or theme. Expect luxe layers, chrome, and hand-painted details, finished for a photo-ready glow.
        <br className="hidden md:block" />
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        {/* House Call */}
        <div className="bg-white rounded-2xl shadow p-6 flex flex-col">
          <div className="text-xs uppercase tracking-wide bg-gray-100 text-gray-700 px-2 py-1 rounded w-fit">
            House Call
          </div>
          <h3 className="text-2xl font-semibold mt-3">1–2 People • Live Set Creation</h3>
          <p className="text-gray-600 mt-2">
            I come to you and create one set per person on-site. Travel fee: <strong>$20</strong> for 1–2 people.
            Set pricing depends on the tier you choose (Classic, Frenchie & Friends, The Museum, or Custom).
          </p>

          <div className="mt-4 text-sm text-gray-700">
            <ul className="list-disc ml-5 space-y-1">
              <li>Atlanta area & pop-ups; other areas by request</li>
              <li>All tools + supplies provided</li>
              <li>Payment via Stripe at booking or on-site</li>
            </ul>
          </div>

          <div className="mt-4">
            <label className="text-sm text-gray-600 block mb-1">Party size</label>
            <select
              className="border rounded p-2"
              value={housePartySize}
              onChange={(e) => setHousePartySize(e.target.value)}
            >
              <option value="1">1 person</option>
              <option value="2">2 people</option>
            </select>
          </div>

          <div className="mt-6">
            <button className="btn-nene" onClick={() => setShowHouseForm((s) => !s)}>
              {showHouseForm ? 'Close Request Form' : 'Request a House Call'}
            </button>
          </div>

          {showHouseForm && (
            <form onSubmit={submitHouse} className="mt-5 grid grid-cols-1 gap-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  className="border rounded p-2"
                  placeholder="Your name *"
                  value={houseForm.name}
                  onChange={(e) => setHouseForm({ ...houseForm, name: e.target.value })}
                />
                <input
                  className="border rounded p-2"
                  placeholder="Email *"
                  type="email"
                  value={houseForm.email}
                  onChange={(e) => setHouseForm({ ...houseForm, email: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  className="border rounded p-2"
                  placeholder="Phone (optional)"
                  value={houseForm.phone}
                  onChange={(e) => setHouseForm({ ...houseForm, phone: e.target.value })}
                />
                <input
                  className="border rounded p-2"
                  type="date"
                  value={houseForm.date}
                  onChange={(e) => setHouseForm({ ...houseForm, date: e.target.value })}
                />
              </div>
              <input
                className="border rounded p-2"
                placeholder="Location / Address *"
                value={houseForm.location}
                onChange={(e) => setHouseForm({ ...houseForm, location: e.target.value })}
              />
              <textarea
                className="border rounded p-2"
                rows={3}
                placeholder="Anything else? (preferred tier, inspo, timing)"
                value={houseForm.notes}
                onChange={(e) => setHouseForm({ ...houseForm, notes: e.target.value })}
              />
              <div className="flex items-center gap-3">
                <button type="submit" className="btn-nene" disabled={loading}>
                  {loading ? 'Sending…' : 'Send Request'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Events */}
        <div className="bg-white rounded-2xl shadow p-6 flex flex-col">
          <div className="text-xs uppercase tracking-wide bg-gray-100 text-gray-700 px-2 py-1 rounded w-fit">
            Events
          </div>
          <h3 className="text-2xl font-semibold mt-3">Pop-Ups & Private Events</h3>
          <p className="text-gray-600 mt-2">
            Bring the live nail bar to your party or activation. Up to <strong>5 guests</strong> at about <strong>1 hour per set</strong>.
            I’ll bring the full setup and create looks on-site.
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600 block mb-1">Guests</label>
              <select
                className="border rounded p-2 w-full"
                value={eventGuestCount}
                onChange={(e) => setEventGuestCount(e.target.value)}
              >
                <option value="2">2 guests</option>
                <option value="3">3 guests</option>
                <option value="4">4 guests</option>
                <option value="5">5 guests</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600 block mb-1">Total Time</label>
              <div className="border rounded p-2 w-full bg-gray-50">
                {totalHours} hr{totalHours > 1 ? 's' : ''}
              </div>
            </div>
          </div>

          <ul className="list-disc ml-5 mt-4 text-gray-700 space-y-1 text-sm">
            <li>I only need a nearby outlet — I provide the rest</li>
            <li>Atlanta & pop-ups; other areas by request (travel fee may apply)</li>
            <li>Sets are priced by tier (Classic / F&F / Museum / Custom)</li>
          </ul>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button className="btn-nene" onClick={() => setShowEventForm((s) => !s)}>
              {showEventForm ? 'Close Request Form' : 'Request an Event'}
            </button>
            <div className="text-sm bg-gray-50 px-3 py-2 rounded border">
              Deposit (optional): <strong>${(depositCents / 100).toFixed(2)}</strong>
            </div>
            <button
              className="btn-nene"
              onClick={payEventDeposit}
              disabled={loading || !isValidEmail(eventForm.email)}
              title={!isValidEmail(eventForm.email) ? 'Enter email in the event form first' : 'Pay deposit'}
            >
              {loading ? 'Starting…' : 'Pay Deposit'}
            </button>
          </div>

          {showEventForm && (
            <form onSubmit={submitEvent} className="mt-5 grid grid-cols-1 gap-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  className="border rounded p-2"
                  placeholder="Your name *"
                  value={eventForm.name}
                  onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                />
                <input
                  className="border rounded p-2"
                  placeholder="Email *"
                  type="email"
                  value={eventForm.email}
                  onChange={(e) => setEventForm({ ...eventForm, email: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  className="border rounded p-2"
                  placeholder="Phone (optional)"
                  value={eventForm.phone}
                  onChange={(e) => setEventForm({ ...eventForm, phone: e.target.value })}
                />
                <input
                  className="border rounded p-2"
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  className="border rounded p-2"
                  type="time"
                  value={eventForm.startTime}
                  onChange={(e) => setEventForm({ ...eventForm, startTime: e.target.value })}
                />
                <input
                  className="border rounded p-2"
                  placeholder="Location / Address *"
                  value={eventForm.location}
                  onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                />
              </div>
              <textarea
                className="border rounded p-2"
                rows={3}
                placeholder="Anything else? (theme, inspo, brand, parking, etc.)"
                value={eventForm.notes}
                onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
              />
              <div className="flex items-center gap-3">
                <button type="submit" className="btn-nene" disabled={loading}>
                  {loading ? 'Sending…' : 'Send Request'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* global message line */}
      {msg && (
        <p className={`mt-4 text-center ${msg.type === 'ok' ? 'text-emerald-600' : 'text-red-600'}`}>
          {msg.text}
        </p>
      )}
    </section>
    </div>
    <Footer />
    </>
  );
};

export default Experience;
